import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import './register.css';
import BASE_URL from '../../config';

// Plain manual entry for address_line_1/city/postcode -- no postcode
// lookup/autocomplete, per David's request to drop the automatic pickup
// and just have the customer type all three fields themselves.
const fields = [
  { name: 'first_name', label: 'First Name', autoComplete: 'given-name' },
  { name: 'last_name', label: 'Last Name', autoComplete: 'family-name' },
  { name: 'email', label: 'Email', type: 'email', autoComplete: 'email' },
  { name: 'password', label: 'Password', type: 'password', autoComplete: 'new-password' },
  { name: 'confirmPassword', label: 'Confirm Password', type: 'password', autoComplete: 'new-password' },
  { name: 'address_line_1', label: 'Address', autoComplete: 'address-line1' },
  { name: 'city', label: 'City', autoComplete: 'address-level2' },
  { name: 'postcode', label: 'Postcode', autoComplete: 'postal-code' },
  { name: 'contact_number', label: 'Contact Number', type: 'tel', autoComplete: 'tel' },
];
const renderField = ({ name, label, type = 'text', autoComplete }, errors, touched) => {
  const hasError = errors[name] && touched[name];
  const fieldId = `register-${name}`;
  const errorId = `${fieldId}-error`;
  return (
    <div key={name} className="form-group">
      <label className="form-label" htmlFor={fieldId}>
        {label}
        <span className="required-indicator" aria-hidden="true"> *</span>
      </label>
      <Field
        id={fieldId}
        name={name}
        type={type}
        className={`form-input ${hasError ? 'form-input-error' : ''}`}
        autoComplete={autoComplete}
        aria-required="true"
        aria-invalid={hasError ? 'true' : 'false'}
        aria-describedby={hasError ? errorId : undefined}
      />
      <ErrorMessage name={name}>
        {(msg) => (
          <div id={errorId} className="error-message" role="alert">
            {msg}
          </div>
        )}
      </ErrorMessage>
    </div>
  );
};

const Register = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { device_key, case_key } = useParams();

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    address_line_1: '',
    city: '',
    postcode: '',
    contact_number: '',
  };

  const validationSchema = Yup.object({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm your password'),
    address_line_1: Yup.string().required('Address is required'),
    city: Yup.string().required('City is required'),
    postcode: Yup.string().required('Postcode is required'),
    contact_number: Yup.string()
      .matches(/^\d+$/, 'Contact number must be numeric')
      .required('Contact number is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const { confirmPassword, ...formData } = values;

    const user = {
      ...formData,
      username: formData.email,
    };

    try {
      const registerUrl = case_key
        ? `${BASE_URL}/api/register/${device_key}/${case_key}/`
        : `${BASE_URL}/api/register/${device_key}/`;
      await axios.post(registerUrl, user);
      setErrorMessage('');

      navigate('/verify-email', {
        state: {
          email: formData.email
        }
      });
    } catch (error) {
      console.error('There was an error registering the user!', error);
      if (error.response && error.response.data) {
        setErrorMessage(error.response.data.message || 'Registration failed. Please try again.');
      } else {
        setErrorMessage('Registration failed. Please check your internet connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!device_key) {
      setErrorMessage('Device key is required.');
    }
  }, [device_key, case_key]);

  return (
    <div className="register-page-wrapper">
      <main className="register-container" role="main">
        <h1 className="register-title">Register</h1>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="register-form" aria-label="Registration form" noValidate>
              {fields.map((f) => renderField(f, errors, touched))}

              <button
                type="submit"
                className="register-btn"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Registering...' : 'Register'}
              </button>
            </Form>
          )}
        </Formik>

        {errorMessage && (
          <div className="error-message form-error" role="alert" aria-live="assertive">
            {errorMessage}
          </div>
        )}

        <div className="login-link">
          <p>Already have an account? <Link to="/signin">Login</Link></p>
        </div>
      </main>
    </div>
  );
};

export default Register;
